import { validateLessonBody } from './validation';
import { LessonBodyValue } from './types';

/**
 * The lesson body while it is being edited. The visual editor works on the parsed object;
 * the JSON editor on raw text that may not parse yet. Switching modes converts between
 * them, and a switch to visual is refused while the text is invalid.
 */
export type BodyDraft =
  | { mode: 'visual'; body: LessonBodyValue | null }
  | { mode: 'json'; text: string };

export type BodyMode = BodyDraft['mode'];

export const EMPTY_DRAFT: BodyDraft = { mode: 'visual', body: null };

/**
 * Opens a stored body in the visual editor, falling back to JSON (with the reason) when the
 * visual editor can't represent it — e.g. a card type this admin doesn't know yet.
 */
export function draftFromBody(body: unknown): { draft: BodyDraft; error: string | null } {
  if (body === null || body === undefined) return { draft: EMPTY_DRAFT, error: null };
  const text = JSON.stringify(body, null, 2);
  const error = validateLessonBody(text);
  if (error) {
    return { draft: { mode: 'json', text }, error: `Opened as JSON — the visual editor can't show this body: ${error}` };
  }
  return { draft: { mode: 'visual', body: body as LessonBodyValue }, error: null };
}

export function toJsonDraft(draft: BodyDraft): BodyDraft {
  if (draft.mode === 'json') return draft;
  return { mode: 'json', text: draft.body ? JSON.stringify(draft.body, null, 2) : '' };
}

export function toVisualDraft(draft: BodyDraft): { draft: BodyDraft } | { error: string } {
  if (draft.mode === 'visual') return { draft };
  if (!draft.text.trim()) return { draft: EMPTY_DRAFT };
  const error = validateLessonBody(draft.text);
  if (error) return { error };
  return { draft: { mode: 'visual', body: JSON.parse(draft.text) } };
}

/** The body to send on save, or why it can't be saved. */
export function resolveDraft(draft: BodyDraft): { body: LessonBodyValue | null } | { error: string } {
  const result = toVisualDraft(draft);
  if ('error' in result) return result;
  return { body: result.draft.mode === 'visual' ? result.draft.body : null };
}
